import clsx from 'clsx';
import { ReactElement } from 'react';
import { BsFillRocketTakeoffFill } from 'react-icons/bs';
import { GoAlertFill } from 'react-icons/go';
import { ImCogs } from 'react-icons/im';
import { MdCancel, MdHelpOutline, MdOutlineSyncProblem } from 'react-icons/md';
import { PiClockCountdownBold } from 'react-icons/pi';
import { RiEyeOffLine } from 'react-icons/ri';

import { Tooltip } from '@/components/tooltip/tooltip';
import { DeploymentStatus } from '@/types/agent';

import styles from './deploymentStatus.module.scss';

type StatusConfig = { label: string; tooltip: string; icon: ReactElement; colorClass: string };

type DeploymentStatusBadgeProps = {
  status: DeploymentStatus;
  className?: string;
  variant?: 'default' | 'large';
};

const STATUS_CONFIG: Record<DeploymentStatus, StatusConfig> = {
  [DeploymentStatus.DEPLOYING]: {
    label: 'Deploying',
    tooltip: 'Agent is being deployed',
    icon: <BsFillRocketTakeoffFill />,
    colorClass: styles.deploying
  },
  [DeploymentStatus.SCHEDULING]: {
    label: 'Updating',
    tooltip: 'Agent is applying updates',
    icon: <PiClockCountdownBold />,
    colorClass: styles.updating
  },
  [DeploymentStatus.STARTING]: {
    label: 'Updating',
    tooltip: 'Agent is applying updates',
    icon: <PiClockCountdownBold />,
    colorClass: styles.updating
  },
  [DeploymentStatus.RESTARTING]: {
    label: 'Updating',
    tooltip: 'Agent is applying updates',
    icon: <PiClockCountdownBold />,
    colorClass: styles.updating
  },
  [DeploymentStatus.FAILED_DEPLOYMENT]: {
    label: 'Platform Error',
    tooltip: 'Agent is unavailable due to a platform issue. Please check back later.',
    icon: <MdCancel />,
    colorClass: styles.failed_deployment
  },
  [DeploymentStatus.RUNNING]: {
    label: 'Running',
    tooltip: 'Agent is online and processing requests',
    icon: <ImCogs />,
    colorClass: styles.running
  },
  [DeploymentStatus.ERRORED]: {
    label: 'Errored',
    tooltip: 'The agent encountered an error and cannot process requests. Check error details for more information.',
    icon: <GoAlertFill />,
    colorClass: styles.errored
  },
  [DeploymentStatus.DEGRADED]: {
    label: 'Degraded',
    tooltip: 'One or more servers are unavailable. The agent is still processing requests.',
    icon: <MdOutlineSyncProblem />,
    colorClass: styles.degraded
  },
  [DeploymentStatus.OFF]: {
    label: 'Off',
    tooltip: 'Agent is turned off',
    icon: <RiEyeOffLine />,
    colorClass: styles.off
  }
};

// unknown state as core can introduce new states not mappped here, crashing agents page
const UNKNOWN_STATUS_CONFIG: StatusConfig = {
  label: 'Unknown',
  tooltip: 'Agent status is unavailable.',
  icon: <MdHelpOutline />,
  colorClass: styles.unknown
};

function resolveStatusConfig( status: DeploymentStatus ): StatusConfig {
  return STATUS_CONFIG[status] ?? UNKNOWN_STATUS_CONFIG;
}

export function getDeploymentStatusTooltip( status: DeploymentStatus ): string {
  return resolveStatusConfig( status ).tooltip;
}

export function DeploymentStatusBadge( {
  status,
  className,
  variant = 'default'
}: DeploymentStatusBadgeProps ): ReactElement {
  const config = resolveStatusConfig( status );

  return (
    <Tooltip
      content={ config.tooltip }
      position="top"
    >
      <div
        className={ clsx( styles.badge, variant === 'large' && styles.badgeLarge, config.colorClass, className ) }
        data-testid="deployment-status"
        data-status={ status }
      >
        <span className={ styles.icon }>{ config.icon }</span>
        <span className={ styles.label }>{ config.label }</span>
      </div>
    </Tooltip>
  );
}
